import React, { useEffect, useRef, useState } from "react";
import { Lock, Loader2, Trash2, CornerDownLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MAX_ENTRY = 5000;
const stamp = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

// Lawyer-only private notes as an append-only feed: Enter saves an entry (Shift+Enter = newline),
// entries can be deleted one at a time (confirmed) but never edited or overwritten.
export function LawyerNotesPane({ cid }) {
  const [entries, setEntries] = useState(null); // null = loading
  const [draft, setDraft] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get(`/consultations/${cid}/lawyer-notes`)
      .then((r) => { if (alive) setEntries(r.data.entries || []); })
      .catch(() => { if (alive) setEntries([]); });
    return () => { alive = false; };
  }, [cid]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries?.length]);

  // Optimistic: the entry appears and the composer clears immediately; a failed save
  // stays in the feed flagged "not saved" with Retry / Remove so nothing typed is lost.
  const persist = async (entry) => {
    try {
      const r = await api.post(`/consultations/${cid}/lawyer-notes`, { content: entry.content });
      setEntries((prev) => (prev || []).map((n) => (n.id === entry.id ? r.data : n)));
    } catch (e) {
      setEntries((prev) => (prev || []).map((n) => (n.id === entry.id ? { ...n, pending: false, failed: true } : n)));
      toast.error(e.response?.data?.detail || "Could not save note");
    }
  };
  const submit = () => {
    const content = draft.trim();
    if (!content || entries === null) return;
    const entry = { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, content, created_at: new Date().toISOString(), pending: true };
    setEntries((prev) => [...(prev || []), entry]);
    setDraft("");
    persist(entry);
  };
  const retry = (n) => {
    setEntries((prev) => (prev || []).map((x) => (x.id === n.id ? { ...x, failed: false, pending: true } : x)));
    persist(n);
  };
  const discardFailed = (n) => setEntries((prev) => (prev || []).filter((x) => x.id !== n.id));

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) { e.preventDefault(); submit(); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/consultations/${cid}/lawyer-notes/${pendingDelete.id}`);
      setEntries((prev) => (prev || []).filter((n) => n.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete note");
    } finally { setDeleting(false); }
  };

  return (
    <div className="box-border flex w-full max-w-full min-w-0 flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 lg:h-full lg:min-h-0" data-testid="lawyer-notes-pane">
      <div className="flex min-w-0 shrink-0 items-center justify-between gap-2">
        <p className="min-w-0 break-words text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 inline-flex items-center gap-1" data-testid="lawyer-notes-private">
          <Lock size={11} /> Private — never visible to the client
        </p>
        {entries && entries.length > 0 && (
          <span className="text-[10px] text-slate-400 whitespace-nowrap shrink-0" data-testid="lawyer-notes-count">{entries.length} note{entries.length > 1 ? "s" : ""}</span>
        )}
      </div>

      <div ref={listRef} className="flex-1 min-h-[160px] max-h-[46vh] lg:max-h-none overflow-y-auto pr-1 flex flex-col gap-2" data-testid="lawyer-notes-list">
        {entries === null && <div className="grid place-items-center py-8 text-slate-400"><Loader2 className="animate-spin" /></div>}
        {entries && entries.length === 0 && (
          <p className="text-xs text-slate-400 leading-relaxed py-6 px-2 text-center" data-testid="lawyer-notes-empty">
            No notes yet. Jot down facts, issues, advice given, follow-ups… each note is saved the moment you press Enter and used as context for the AI memo and LCR.
          </p>
        )}
        {entries?.map((n) => (
          <div key={n.id} className="group relative rounded-2xl rounded-bl-md bg-[#FBF4DF] border border-[#EAD9A6] px-3 py-2 text-sm text-[#0A2540]" data-testid="lawyer-note-entry" data-note-id={n.id}>
            <p className="whitespace-pre-wrap break-words" data-testid="lawyer-note-content">{n.content}</p>
            <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-slate-500">
              <span data-testid="lawyer-note-time" data-state={n.failed ? "failed" : n.pending ? "pending" : "saved"}>
                {n.failed ? <span className="text-rose-600 font-bold">Not saved</span> : n.pending ? <span className="inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Saving…</span> : stamp(n.created_at)}
                {n.legacy ? " · earlier notes" : ""}
              </span>
              {n.failed ? (
                <span className="inline-flex items-center gap-2">
                  <button type="button" onClick={() => retry(n)} data-testid="lawyer-note-retry" className="font-bold text-[#0A2540] hover:underline">Retry</button>
                  <button type="button" onClick={() => discardFailed(n)} data-testid="lawyer-note-discard" className="text-slate-400 hover:text-rose-600">Remove</button>
                </span>
              ) : !n.pending && (
                <button type="button" onClick={() => setPendingDelete(n)} aria-label="Delete note" data-testid="lawyer-note-delete"
                  className="inline-flex items-center gap-1 text-slate-400 hover:text-rose-600 opacity-60 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="shrink-0 rounded-2xl border border-slate-200 bg-slate-50 focus-within:border-[#0A2540] transition-colors">
        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown} rows={2} maxLength={MAX_ENTRY}
          disabled={entries === null} data-testid="lawyer-notes-input"
          placeholder="Type a note and press Enter to save…"
          className="text-sm border-0 bg-transparent shadow-none focus-visible:ring-0 resize-none min-h-[44px] max-h-40" />
        <div className="flex items-center justify-between px-3 pb-2 text-[10px] text-slate-400">
          <span className="inline-flex items-center gap-1 truncate"><CornerDownLeft size={10} className="shrink-0" /> Enter saves · Shift+Enter = new line</span>
          <button type="button" onClick={submit} disabled={!draft.trim() || entries === null} data-testid="lawyer-notes-save"
            className="font-bold text-[#0A2540] disabled:text-slate-300 whitespace-nowrap shrink-0 ml-2">Save</button>
        </div>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="lawyer-note-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-wrap break-words">
              “{(pendingDelete?.content || "").slice(0, 200)}{(pendingDelete?.content || "").length > 200 ? "…" : ""}”
              {"\n"}This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lawyer-note-delete-cancel" disabled={deleting}>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} data-testid="lawyer-note-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700">
              {deleting ? <Loader2 size={14} className="animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
